import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyDecks, deleteSavedDeck, type SavedDeckSummary } from '../lib/api';

function fmtDate(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

export function MyDecksPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; decks: SavedDeckSummary[] }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const load = (): void => {
    setState({ status: 'loading' });
    fetchMyDecks()
      .then((r) => setState({ status: 'ready', decks: r.decks }))
      .catch((e) => setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }));
  };

  useEffect(load, []);

  const handleDelete = async (id: string, name: string): Promise<void> => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await deleteSavedDeck(id);
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not delete the deck.');
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-vari-beige/20">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div>
          <Link to="/" className="text-sm text-vari-blue-accessible hover:underline">
            ← Back to catalog
          </Link>
          <h1 className="text-3xl font-semibold text-vari-navy mt-2">My decks</h1>
          <p className="text-vari-gray text-sm mt-1">
            Decks you've exported or shared. Open one to review it, re-download, or load it back to edit —
            rename and add internal notes for your own reference.
          </p>
        </div>

        {state.status === 'loading' && <p className="text-vari-gray text-sm">Loading…</p>}

        {state.status === 'error' && (
          <div className="rounded-lg border border-vari-red/30 bg-vari-red/5 px-5 py-4 text-sm text-vari-navy">
            {state.message}
          </div>
        )}

        {state.status === 'ready' && state.decks.length === 0 && (
          <div className="rounded-2xl border border-vari-soft-gray bg-white p-10 text-center">
            <p className="text-vari-navy font-semibold text-lg">No saved decks yet</p>
            <p className="text-vari-gray text-sm mt-2 max-w-md mx-auto">
              When you export or share a deck it's saved here so you can find it later.
            </p>
            <Link
              to="/"
              className="inline-block mt-6 rounded-lg bg-vari-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors"
            >
              Browse the catalog
            </Link>
          </div>
        )}

        {state.status === 'ready' && state.decks.length > 0 && (
          <div className="space-y-3">
            {state.decks.map((d) => (
              <Link
                key={d.id}
                to={`/my-decks/${d.id}`}
                className="block rounded-2xl border border-vari-soft-gray bg-white p-5 hover:border-vari-navy transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-vari-navy truncate">{d.name}</p>
                    <p className="text-vari-gray text-xs mt-1">
                      {fmtDate(d.createdAt)} · {d.layoutCount} layout{d.layoutCount === 1 ? '' : 's'} ·{' '}
                      {d.imageCount} image{d.imageCount === 1 ? '' : 's'}
                    </p>
                    {d.notes && <p className="text-vari-gray text-sm mt-2 line-clamp-2">{d.notes}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void handleDelete(d.id, d.name);
                    }}
                    className="text-vari-red text-sm hover:underline shrink-0"
                  >
                    Delete
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
